import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { HiOutlineMail, HiArrowLeft } from 'react-icons/hi';
import { useAuth } from '~/contexts/AuthContext';
import { requestOTP, verifyOTP, getOTPErrorMessage } from '~/lib/network/auth';

type Step = 'email' | 'otp';

export default function EmailAuthModal() {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const modalRef = useRef<HTMLDialogElement>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const openModal = () => {
    modalRef.current?.showModal();
  };

  const closeModal = () => {
    modalRef.current?.close();
    resetForm();
  };

  const resetForm = () => {
    setStep('email');
    setEmail('');
    setOtp('');
    setError('');
    setResendCooldown(0);
  };

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await requestOTP({ email: email.trim().toLowerCase() });
      setStep('otp');
      setResendCooldown(60);
      // Focus OTP input after transition
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (err: any) {
      setError(getOTPErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.replace(/\D/g, '');
    if (code.length !== 6) return;

    setLoading(true);
    setError('');

    try {
      await verifyOTP({ email: email.trim().toLowerCase(), code });
      await refreshAuth();
      closeModal();
      navigate('/app');
    } catch (err: any) {
      setError(getOTPErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');

    try {
      await requestOTP({ email: email.trim().toLowerCase() });
      setResendCooldown(60);
      setOtp('');
      otpInputRef.current?.focus();
    } catch (err: any) {
      setError(getOTPErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value: string) => {
    // Only allow digits, max 6
    const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
    setOtp(digitsOnly);
  };

  return (
    <>
      {/* Trigger Button */}
      <button onClick={openModal} className="btn btn-ghost btn-sm text-sm opacity-70">
        <HiOutlineMail size={16} />
        Sign in with email
      </button>

      {/* Modal */}
      <dialog ref={modalRef} className="modal modal-bottom sm:modal-middle">
        <div className="modal-box w-full max-w-md p-6 relative">
          {/* Close button */}
          <button
            type="button"
            onClick={closeModal}
            aria-label="Close"
            className="btn btn-sm btn-circle btn-ghost absolute right-3 top-3"
          >
            ✕
          </button>

          {step === 'email' ? (
            <>
              <h3 className="text-xl font-semibold mb-2">Sign in with email</h3>
              <p className="text-sm opacity-60 mb-6">We'll send you a verification code</p>

              <form onSubmit={handleRequestOTP} className="space-y-4">
                <div>
                  <label className="label py-1" htmlFor="auth-email">
                    <span className="label-text">Email</span>
                  </label>
                  <input
                    id="auth-email"
                    type="email"
                    placeholder="you@example.com"
                    className="input input-bordered w-full"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    disabled={loading}
                  />
                </div>

                {error && <div className="text-error text-sm">{error}</div>}

                <button type="submit" disabled={loading || !email} className="btn btn-primary w-full">
                  {loading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Sending code...
                    </>
                  ) : (
                    'Send code'
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Back button */}
              <button
                type="button"
                onClick={() => { setStep('email'); setOtp(''); setError(''); }}
                className="btn btn-ghost btn-sm absolute left-3 top-3"
              >
                <HiArrowLeft size={16} />
              </button>

              <h3 className="text-xl font-semibold mb-2 mt-4">Enter code</h3>
              <p className="text-sm opacity-60 mb-6">
                We sent a 6-digit code to <span className="font-medium">{email}</span>
              </p>

              <form onSubmit={handleVerifyOTP} className="space-y-4">
                {/* Single OTP Input */}
                <div>
                  <input
                    ref={otpInputRef}
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter verification code"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => handleOtpChange(e.target.value)}
                    className="input input-bordered w-full text-center"
                    disabled={loading}
                    autoComplete="one-time-code"
                  />
                </div>

                {error && <div className="text-error text-sm text-center">{error}</div>}

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="btn btn-primary w-full"
                >
                  {loading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </button>
              </form>

              {/* Resend option */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resendCooldown > 0 || loading}
                  className="btn btn-ghost btn-sm text-sm"
                >
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Didn't receive code? Resend"}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Click outside to close */}
        <div className="modal-backdrop" onClick={closeModal}>
          <button type="button" className="cursor-default">
            close
          </button>
        </div>
      </dialog>
    </>
  );
}
